import { yupResolver } from '@hookform/resolvers/yup';
import { sendGTMEvent } from '@next/third-parties/google';
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { AuthLoadingOverlay, SsoProviders } from '@/components/modals/sso';
import { AlreadyLoggedInNotice } from '@/components/trial/already-logged-in-notice';
import type { AlertStateOrEmpty } from '@/components/ui/alert';
import { Alert } from '@/components/ui/alert';
import CaptchaProvider from '@/components/ui/captcha-provider';
import type { CreateUserTrialParams } from '@/context/types';
import { useAuth } from '@/hooks/use-auth';
import { apiErrorMessageKey, TRIAL_STATUS_KEYS } from '@/utils/api-error-i18n';
import { personNameSchema } from '@/utils/person-name';
import { getUtmData } from '@/utils/user-tracking-data';

import { V1A_NAMESPACE } from '../constants';
import styles from './form.module.css';

export interface SignupFormProps {
  trialConfigId: number | null;
  trialPageId: number | null;
  isDisabledButton?: boolean;
  /** Distinct per instance (hero card / dialog): every field id is derived from it. */
  formId: string;
}

interface FormValues {
  name: string;
  email: string;
  repeatEmail: string;
  password: string;
  passwordRepeat: string;
  recaptcha: string;
}

type FieldName = Exclude<keyof FormValues, 'recaptcha'>;

const DEFAULT_VALUES: FormValues = { name: '', email: '', repeatEmail: '', password: '', passwordRepeat: '', recaptcha: '' };
const FIELD_ORDER: FieldName[] = ['name', 'email', 'repeatEmail', 'password', 'passwordRepeat'];
/** The titles the live noping.com home lists as supported: names, never translated. */
const FORM_GAMES = ['Valorant', 'CS2', 'League of Legends', 'Fortnite', 'Apex Legends'];
/** Same floor the site's other trial forms enforce (create-account-v2): the API accepts it. */
const PASSWORD_MIN = 6;

function OkIcon() {
  return (
    <svg className='np-field__ok' viewBox='0 0 24 24' width='24' height='24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
      <circle cx='12' cy='12' r='9' />
      <path d='m8.4 12.3 2.4 2.4 4.8-5' />
    </svg>
  );
}

function EyeIcons() {
  return (
    <>
      <svg className='ico-on' viewBox='0 0 24 24' width='24' height='24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
        <path d='M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z' />
        <circle cx='12' cy='12' r='3' />
      </svg>
      <svg className='ico-off' viewBox='0 0 24 24' width='24' height='24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
        <path d='M3 3l18 18' />
        <path d='M10.6 6.1A9.8 9.8 0 0 1 12 6c6 0 9.5 6 9.5 6a16.6 16.6 0 0 1-3.2 3.7' />
        <path d='M6.6 6.7A16.4 16.4 0 0 0 2.5 12S6 18 12 18a9.6 9.6 0 0 0 4.2-.9' />
        <path d='M9.9 9.9a3 3 0 0 0 4.2 4.2' />
      </svg>
    </>
  );
}

function ErrorIcon() {
  return (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
      <circle cx='12' cy='12' r='9' />
      <path d='M12 7.8v4.9' />
      <path d='M12 16.2h.01' />
    </svg>
  );
}

/**
 * The design's `.np-form` card ("Teste grátis sem cartão") wired to the site's real sign-up: the same
 * contract as `create-account-v2` — `useAuth().createTrial` with `custom` (= the trial CONFIG id) and
 * `trialPageId`, UTM data, the captcha token, SSO providers carrying the same trial context, the
 * already-logged-in path, the Alert pop-up for API errors, then login + `/thanks-for-trial-no-card`.
 * The design's `np:trial-submit` event and its "Pronto para integração" note are replaced by that flow.
 */
export function SignupForm({ trialConfigId, trialPageId, isDisabledButton = false, formId }: SignupFormProps) {
  const router = useRouter();
  const auth = useAuth();
  const { t } = useTranslation([V1A_NAMESPACE, 'common']);
  const [isLoading, setIsLoading] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState<Record<'password' | 'passwordRepeat', boolean>>({ password: false, passwordRepeat: false });
  const [popUp, setPopUp] = useState<{ show: boolean; message: string; state: AlertStateOrEmpty }>({ show: false, message: '', state: '' });
  const [isSsoAuthenticating, setIsSsoAuthenticating] = useState(false);
  const captchaResetRef = useRef<(() => void) | null>(null);
  const resetCaptcha = useCallback(() => captchaResetRef.current?.(), []);

  const handleSsoStart = useCallback(() => setIsSsoAuthenticating(true), []);
  const handleSsoSuccess = useCallback(() => setIsSsoAuthenticating(false), []);
  const handleSsoError = useCallback((message: string) => {
    setPopUp({ show: true, message, state: 'error' });
    setIsSsoAuthenticating(false);
  }, []);

  const schema = yup.object().shape({
    name: personNameSchema(t('form.errors.required'), t('form.errors.name')),
    email: yup.string().required(t('form.errors.required')).email(t('form.errors.email')).max(255),
    repeatEmail: yup
      .string()
      .required(t('form.errors.required'))
      .email(t('form.errors.email'))
      .max(255)
      .oneOf([yup.ref('email')], t('form.errors.email_match')),
    password: yup
      .string()
      .required(t('form.errors.required'))
      .min(PASSWORD_MIN, t('form.errors.password_min', { count: PASSWORD_MIN })),
    passwordRepeat: yup
      .string()
      .required(t('form.errors.required'))
      .oneOf([yup.ref('password')], t('form.errors.password_match')),
    recaptcha: yup.string().required(t('form.errors.captcha')),
  });

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    reset,
    watch,
    formState: { errors, touchedFields, isSubmitted },
  } = useForm<FormValues>({ resolver: yupResolver(schema), defaultValues: DEFAULT_VALUES, mode: 'onTouched' });

  const values = watch();

  const handleRecaptchaChange = useCallback(
    (token: string | null) => {
      setValue('recaptcha', token ?? '');
      clearErrors('recaptcha');
    },
    [setValue, clearErrors]
  );

  const handleRecaptchaExpired = useCallback(() => {
    setValue('recaptcha', '');
    setError('recaptcha', { type: 'required', message: t('form.errors.captcha') });
  }, [setValue, setError, t]);

  const handleRecaptchaError = useCallback(() => {
    setValue('recaptcha', '');
    resetCaptcha();
  }, [setValue, resetCaptcha]);

  const onSubmit = async (data: FormValues) => {
    if (isDisabledButton) return;
    const { email, password, passwordRepeat, recaptcha, name } = data;
    setIsLoading(true);
    const utmData = getUtmData();

    try {
      const body: CreateUserTrialParams = {
        email,
        password,
        password_repeat: passwordRepeat,
        recaptcha,
        ref: null,
        name,
        // `custom` IS the trial config id — the endpoint's name for it. Both ids have to reach the API or the
        // account is created with no trial at all, and it still answers 201.
        custom: trialConfigId,
        trialPageId,
        ...utmData,
      };

      await auth.createTrial(
        body,
        (error) => {
          setValue('recaptcha', '');
          resetCaptcha();
          setPopUp({ show: true, message: t(apiErrorMessageKey(error, { fallbackKey: 'common:login.mensage_error_create_user_generic', statusKeys: TRIAL_STATUS_KEYS })), state: 'error' });
          setIsLoading(false);
        },
        () => {
          auth.login({ login: email, password, recaptcha });
          sendGTMEvent({ event: 'createAccount' });
          reset();
          sessionStorage.setItem('customTrialApproved', 'true');
          router.push('/thanks-for-trial-no-card');
          setIsLoading(false);
        },
        false
      );
    } catch {
      setValue('recaptcha', '');
      resetCaptcha();
      setPopUp({ show: true, message: t('common:login.mensage_error_create_user_generic'), state: 'error' });
      setIsLoading(false);
    }
  };

  // A logged-in visitor never sees a "create a new account" form: the notice checks eligibility (SCRUM-1663)
  // and offers to start the SAME trial from the existing account.
  if (auth.user) {
    return (
      <div className={styles.notice}>
        <AlreadyLoggedInNotice trialConfigId={trialConfigId} trialPageId={trialPageId} />
      </div>
    );
  }

  const invalidCount = FIELD_ORDER.filter((field) => errors[field]).length;
  const summary = isSubmitted && invalidCount > 0 ? t('form.fix', { count: invalidCount }) : '';
  const titleId = `${formId}-title`;

  const renderField = (field: FieldName, options: { type: 'text' | 'password'; autoComplete: string; inputMode?: 'email'; hint?: ReactNode; minLength?: number }) => {
    const inputId = `${formId}-${field}`;
    const messageId = `${inputId}-msg`;
    const hintId = options.hint ? `${inputId}-hint` : undefined;
    const error = errors[field]?.message;
    const isPassword = options.type === 'password';
    const passwordField = field === 'password' || field === 'passwordRepeat' ? field : null;
    const isVisible = passwordField ? visiblePasswords[passwordField] : false;
    const isValid = !error && Boolean(touchedFields[field]) && values[field].trim().length > 0;
    const label = (
      <label className='np-field__label' htmlFor={inputId}>
        {t(`form.fields.${field}.label`)}
        <OkIcon />
      </label>
    );

    return (
      <div className={classNames('np-field', isPassword && 'np-field--password', error && 'np-field--invalid', isValid && 'np-field--valid')}>
        {options.hint ? (
          <div className='np-field__top'>
            {label}
            <span className='np-field__hint' id={hintId}>
              {options.hint}
            </span>
          </div>
        ) : (
          label
        )}
        <div className='np-field__control'>
          <input className='np-field__input' id={inputId} type={isPassword && !isVisible ? 'password' : 'text'} inputMode={options.inputMode} maxLength={255} minLength={options.minLength} autoComplete={options.autoComplete} dir={options.autoComplete === 'name' ? 'auto' : undefined} autoCapitalize={options.inputMode === 'email' ? 'off' : undefined} spellCheck={options.inputMode === 'email' ? false : undefined} placeholder={t(`form.fields.${field}.placeholder`)} aria-required='true' aria-invalid={error ? 'true' : undefined} aria-describedby={[hintId, messageId].filter(Boolean).join(' ')} data-lpignore='true' {...register(field)} />
          {passwordField && (
            <button className='np-field__toggle' type='button' aria-controls={inputId} data-visible={String(isVisible)} aria-label={t(`form.fields.${passwordField}.${isVisible ? 'hide' : 'show'}`)} onClick={() => setVisiblePasswords((previous) => ({ ...previous, [passwordField]: !previous[passwordField] }))}>
              <EyeIcons />
            </button>
          )}
        </div>
        <span className={classNames('np-field__msg', error && 'is-error')} id={messageId}>
          {error ?? ''}
        </span>
      </div>
    );
  };

  return (
    <form className='np-form' id={formId} method='post' noValidate autoComplete='on' aria-labelledby={titleId} onSubmit={handleSubmit(onSubmit)}>
      <AuthLoadingOverlay isActive={isSsoAuthenticating} />
      {popUp.show && <Alert message={popUp.message} state={popUp.state} onClose={() => setPopUp({ ...popUp, show: false })} />}

      <div className='np-form__head'>
        <h2 className='np-form__title' id={titleId}>
          <Trans t={t} i18nKey='form.title' components={{ hl: <span className='np-form__hl' /> }} />
        </h2>
        <p className='np-form__sub'>
          {t('form.sub_a')}{' '}
          <span className='dot' aria-hidden='true'>
            ·
          </span>{' '}
          {t('form.sub_b')}
        </p>
      </div>

      <p className='np-form__live sr-only' role='status' aria-live='polite'>
        {summary}
      </p>

      <div className={styles.sso}>
        <SsoProviders onAuthStart={handleSsoStart} onAuthSuccess={handleSsoSuccess} onAuthError={handleSsoError} trialContext={{ custom: trialConfigId, trialPageId }} />
      </div>

      <div className='np-form__fields'>
        {renderField('name', { type: 'text', autoComplete: 'name' })}
        <div className='np-form__row np-form__row--2'>
          {renderField('email', { type: 'text', autoComplete: 'email', inputMode: 'email' })}
          {renderField('repeatEmail', { type: 'text', autoComplete: 'email', inputMode: 'email' })}
        </div>
        <div className='np-form__row np-form__row--2'>
          {renderField('password', { type: 'password', autoComplete: 'new-password', minLength: PASSWORD_MIN, hint: t('form.fields.password.hint', { count: PASSWORD_MIN }) })}
          {renderField('passwordRepeat', { type: 'password', autoComplete: 'new-password', minLength: PASSWORD_MIN })}
        </div>
      </div>

      <div className='np-form__turnstile'>
        <CaptchaProvider theme='dark' onChange={handleRecaptchaChange} onExpired={handleRecaptchaExpired} onError={handleRecaptchaError} resetRef={captchaResetRef} />
      </div>
      {errors.recaptcha?.message && (
        <p className={styles.captchaError} role='alert'>
          {errors.recaptcha.message}
        </p>
      )}

      <div className={classNames('np-form__status np-form__status--top', summary && 'is-visible')} data-state={summary ? 'error' : undefined}>
        <div>
          {summary && (
            <>
              <ErrorIcon />
              <span>{summary}</span>
            </>
          )}
        </div>
      </div>

      <button className={classNames('btn btn-primary btn--block np-form__submit', isLoading && 'is-busy')} type='submit' disabled={isDisabledButton || isLoading} aria-busy={isLoading || undefined}>
        <span className='np-form__submit-label'>
          {isLoading ? (
            t('form.submit_busy')
          ) : (
            <>
              {t('form.submit_lead')}{' '}
              <span className='np-form__submit-end'>
                {t('form.submit_tail')}
                <svg className='np-form__submit-arrow' viewBox='0 0 24 24' width='24' height='24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
                  <path d='M5 12h14' />
                  <path d='m13 6 6 6-6 6' />
                </svg>
              </span>
            </>
          )}
        </span>
        <svg className='np-form__submit-spin' viewBox='0 0 24 24' width='24' height='24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' aria-hidden='true' focusable='false'>
          <path d='M12 3a9 9 0 1 1-6.4 2.6' opacity='.35' />
          <path d='M12 3a9 9 0 0 1 9 9' />
        </svg>
      </button>

      <ul className='np-form__games' aria-label={t('form.games_label')}>
        {FORM_GAMES.map((game) => (
          <li key={game}>{game}</li>
        ))}
      </ul>

      <p className='np-form__legal'>
        <Trans
          t={t}
          i18nKey='form.legal'
          components={{
            terms: <Link href='/terms/terms-of-use' target='_blank' rel='noopener' />,
            privacy: <Link href='/terms/privacy-policy' target='_blank' rel='noopener' />,
            sr: <span className='sr-only' />,
          }}
        />
      </p>
    </form>
  );
}
